import { useEffect, useRef } from 'react';
import { Mail, Globe, ShieldOff } from 'lucide-react';
import styles from './AuthConfigTab.module.css';
import OtpProviderConfig from './OtpProviderConfig';
import { useProvidersStore } from '../../../../../store/providersStore';
import { renderMarkdownToHtml } from '../../../../../shared/widget/markdown';

export interface AuthConfigValues {
  authProvider: string;
  otpEnabled: boolean;
  otpGreeting: string;
  providerGreetings: Record<string, string>;
  smtpHost: string;
  smtpPort: number;
  smtpUser: string;
  smtpPass: string;
  smtpFrom: string;
}

interface Props {
  config: AuthConfigValues;
  onChange: (partial: Partial<AuthConfigValues>) => void;
}

export default function AuthConfigTab({ config, onChange }: Props) {
  const { safeProviders, loading: loadingSso, fetchProviders } = useProvidersStore();
  const greetingRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (safeProviders.length === 0) fetchProviders();
  }, []);

  const authProvider = config.authProvider || 'none';
  const requiresAuth = authProvider !== 'none';

  const handleProviderSelect = (provider: string) => {
    onChange({ authProvider: provider, otpEnabled: provider === 'otp' });
  };

  const providerGreeting = config.providerGreetings?.[authProvider] || '';

  const updateProviderGreeting = (value: string) => {
    onChange({ providerGreetings: { ...config.providerGreetings, [authProvider]: value } });
  };

  const wrapGreetingSelection = (before: string, after: string) => {
    const ta = greetingRef.current;
    if (!ta) return;

    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = providerGreeting.slice(start, end);
    const newText = providerGreeting.slice(0, start) + before + selected + after + providerGreeting.slice(end);

    updateProviderGreeting(newText);
    requestAnimationFrame(() => {
      ta.focus();
      const cursorPos = start + before.length + selected.length + after.length;
      ta.setSelectionRange(cursorPos, cursorPos);
    });
  };

  return (
    <>
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Authentication</h3>
        <div className={styles.field}>
          <label>Require authentication</label>
          <div className={styles.toggleRow}>
            <span className={styles.toggleLabel}>Anonymous</span>
            <button
              className={`${styles.authSwitch} ${requiresAuth ? styles.authSwitchOn : ''}`}
              onClick={() => {
                if (requiresAuth) {
                  onChange({ authProvider: 'none', otpEnabled: false });
                } else {
                  onChange({ authProvider: 'otp', otpEnabled: true });
                }
              }}
              type="button"
              role="switch"
              aria-checked={requiresAuth}
            >
              <span className={styles.authSwitchThumb} />
            </button>
            <span className={styles.toggleLabel}>Required</span>
            <span className={styles.toggleHint}>
              {requiresAuth
                ? 'Users must authenticate before chatting'
                : 'Anyone can use the chat widget without signing in'}
            </span>
          </div>
        </div>
      </div>

      {requiresAuth && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Authentication Provider</h3>
          <div className={styles.authProviderGrid}>
            <div
              className={`${styles.authProviderCard} ${authProvider === 'otp' ? styles.authProviderCardActive : ''}`}
              onClick={() => handleProviderSelect('otp')}
            >
              <div className={styles.authProviderIcon} style={{ background: '#ede9fe', color: '#7c3aed' }}>
                <Mail size={20} />
              </div>
              <span className={styles.authProviderName}>Email OTP</span>
              <span className={styles.authProviderDesc}>Verify users with a 6-digit code sent to their email</span>
            </div>

            {safeProviders.filter(p => p.active).map(p => (
              <div
                key={p.name}
                className={`${styles.authProviderCard} ${authProvider === p.name ? styles.authProviderCardActive : ''}`}
                onClick={() => handleProviderSelect(p.name)}
              >
                <div className={styles.authProviderIcon} style={{ background: '#dbeafe', color: '#2563eb' }}>
                  <Globe size={20} />
                </div>
                <span className={styles.authProviderName}>{p.name.toUpperCase()}</span>
                <span className={styles.authProviderDesc}>Single Sign-On via {p.name.toUpperCase()}</span>
              </div>
            ))}

            {loadingSso && (
              <div className={styles.authProviderCard} style={{ opacity: 0.5, cursor: 'default' }}>
                <div className={styles.authProviderIcon} style={{ background: '#f3f4f6', color: '#9ca3af' }}><Globe size={20} /></div>
                <span className={styles.authProviderDesc}>Loading SSO providers...</span>
              </div>
            )}

            {!loadingSso && safeProviders.filter(p => p.active).length === 0 && (
              <div className={styles.authProviderCard} style={{ opacity: 0.5, cursor: 'default', borderStyle: 'dashed' }}>
                <div className={styles.authProviderIcon} style={{ background: '#f3f4f6', color: '#9ca3af' }}><ShieldOff size={20} /></div>
                <span className={styles.authProviderDesc}>No SSO providers configured. Add one in Settings &rarr; SSO.</span>
              </div>
            )}
          </div>
        </div>
      )}

      {requiresAuth && authProvider === 'otp' && (
        <OtpProviderConfig config={config} onChange={onChange} />
      )}

      {requiresAuth && authProvider !== 'otp' && authProvider !== 'none' && (
        <>
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>{authProvider.toUpperCase()} Greeting</h3>
            <div className={styles.field}>
              <label>Welcome message shown before SSO login</label>
              <div className={styles.greetingEditor}>
                <div className={styles.greetingToolbar}>
                  <button type="button" className={styles.greetingToolBtn} title="Bold"
                    onMouseDown={e => { e.preventDefault(); wrapGreetingSelection('**', '**'); }}><strong>B</strong></button>
                  <button type="button" className={styles.greetingToolBtn} title="Italic"
                    onMouseDown={e => { e.preventDefault(); wrapGreetingSelection('*', '*'); }}><em>I</em></button>
                  <button type="button" className={styles.greetingToolBtn} title="Line break"
                    onMouseDown={e => { e.preventDefault(); wrapGreetingSelection('\n', ''); }}>↵</button>
                </div>
                <textarea ref={greetingRef} rows={3} value={providerGreeting}
                  onChange={e => updateProviderGreeting(e.target.value)}
                  placeholder="Supports **bold**, *italic*, and line breaks" />
                {providerGreeting && (
                  <div className={styles.greetingPreview}>
                    <span className={styles.greetingPreviewLabel}>Preview</span>
                    <div dangerouslySetInnerHTML={{ __html: renderMarkdownToHtml(providerGreeting) }} />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className={styles.section}>
            <div className={styles.authSsoNote}>
              <Globe size={16} style={{ flexShrink: 0, marginTop: 2 }} />
              <div>
                This widget will use the system SSO configuration for <strong>{authProvider.toUpperCase()}</strong>.
                Manage SSO credentials in <a href="/settings">Settings &rarr; SSO Configuration</a>.
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
